import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'
import { getCollegeByAdminEmail, CollegeUser } from '@/utils/userAccess'

export function translateNumberToBengali(numStr: string | number): string {
  const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return String(numStr).replace(/[0-9]/g, w => bengaliDigits[Number(w)]);
}

export function getCollegeAcronym(name: string | null | undefined): string {
  if (!name) return 'কলেজ'
  const words = name.trim().split(/[\s,-]+/).filter(Boolean)
  let acronym = ''
  for (const word of words) {
    if (word.length > 0) {
      let char = word.charAt(0)
      if (word.length > 1 && /[\u09BE-\u09CC]/.test(word.charAt(1))) {
        char += word.charAt(1)
      }
      acronym += char
    }
  }
  return acronym || 'কলেজ'
}

export function translateGroupToBengali(group: string | null | undefined): string {
  if (!group) return 'বিজ্ঞান'
  const g = group.trim().toLowerCase()
  if (g.includes('science') || g.includes('বিজ্ঞান')) return 'বিজ্ঞান'
  if (g.includes('humanities') || g.includes('মানবিক') || g.includes('arts')) return 'মানবিক'
  if (g.includes('business') || g.includes('commerce') || g.includes('ব্যবসায়')) return 'ব্যবসায় শিক্ষা'
  return group
}

interface StudentInfo {
  student_iid: number
  student_name_en?: string
  student_name_bn?: string
  father_name_en?: string
  father_name_bn?: string
  mother_name_en?: string
  mother_name_bn?: string
  class_roll?: number
  group?: string
  session?: string
  ssc_roll?: number
  ssc_board?: string
  ssc_year?: number
}

interface TestimonialRequest {
  id: number
  created_at: string
  student_iid: number
  gpa?: string
  status: 'pending' | 'approved' | 'rejected'
  rejection_reason?: string
  approved_at?: string
  approved_by?: string
  certificate_no?: string
  college_eiin: number | string
  student?: StudentInfo | null
}

export default function TestimonialPage() {
  const navigate = useNavigate()
  const [college, setCollege] = useState<CollegeUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [requests, setRequests] = useState<TestimonialRequest[]>([])
  const [statusTab, setStatusTab] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')

  // Printable Certificate State
  const [selectedCert, setSelectedCert] = useState<TestimonialRequest | null>(null)

  // Application Modal / Form State
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [studentsList, setStudentsList] = useState<StudentInfo[]>([])
  const [selectedStudentId, setSelectedStudentId] = useState<string>('')
  const [gpaInput, setGpaInput] = useState<string>('5.00')
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  // Load college and applications
  const loadData = async () => {
    setLoading(true)
    try {
      const { data: authData } = await supabase.auth.getUser()
      const user = authData.user
      if (!user) {
        navigate('/login', { replace: true })
        return
      }

      const col = await getCollegeByAdminEmail(user.email)
      if (!col) {
        alert('College profile not found for your admin email!')
        setLoading(false)
        return
      }

      setCollege(col)

      // Fetch minimal testimonial requests joined with database_fgc
      const { data, error } = await supabase
        .from('student_testimonial_requests')
        .select('*, student:database_fgc(*)')
        .eq('college_eiin', col.college_eiin)
        .order('id', { ascending: false })

      if (!error && data) {
        setRequests(data as TestimonialRequest[])
      }

      // Load students list for dropdown picker
      const { data: stData } = await supabase
        .from('database_fgc')
        .select('student_iid, student_name_en, student_name_bn, father_name_en, father_name_bn, mother_name_en, mother_name_bn, class_roll, group, session, ssc_roll, ssc_board, ssc_year')
        .eq('college_eiin', col.college_eiin)
        .order('class_roll', { ascending: true })

      if (stData) setStudentsList(stData as StudentInfo[])
    } catch (err: any) {
      console.error('TestimonialPage load error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Directly trigger PDF download (2 pages: Bangla & English)
  const handleDirectPrintPdf = (req: TestimonialRequest) => {
    setSelectedCert(req)
    setTimeout(() => {
      const element = document.getElementById('printable-testimonial-area')
      if (element && (window as any).html2pdf) {
        const opt = {
          margin: 0,
          filename: `Testimonial_${req.student?.student_name_en || req.student?.student_name_bn || 'Certificate'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: false, allowTaint: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] }
        };
        (window as any).html2pdf().set(opt).from(element).save().catch((err: any) => {
          console.error('html2pdf error:', err)
          window.print()
        })
      } else {
        window.print()
      }
    }, 400)
  }

  // Create Application
  const handleCreateApplication = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!college) return
    if (!selectedStudentId) {
      setNotice({ type: 'err', text: 'Please select a student' })
      return
    }

    setSubmitting(true)
    setNotice(null)

    try {
      const payload = {
        student_iid: Number(selectedStudentId),
        gpa: gpaInput.trim() || '5.00',
        status: 'pending',
        college_eiin: college.college_eiin
      }

      const { error } = await supabase
        .from('student_testimonial_requests')
        .insert([payload])

      if (error) throw error

      setNotice({ type: 'ok', text: 'Testimonial application submitted successfully!' })
      setShowApplyModal(false)
      setSelectedStudentId('')
      setGpaInput('5.00')
      await loadData()
    } catch (err: any) {
      console.error('Create application error:', err)
      setNotice({ type: 'err', text: err?.message || 'Failed to submit application' })
    } finally {
      setSubmitting(false)
    }
  }

  // Approve Request
  const handleApprove = async (req: TestimonialRequest) => {
    if (!college) return
    const stName = req.student?.student_name_bn || req.student?.student_name_en || `Student #${req.student_iid}`
    if (!confirm(`Approve testimonial certificate for ${stName}?`)) return

    try {
      const prefix = getCollegeAcronym(college.college_name)
      const certNo = `${prefix}/${college.college_eiin}/${new Date().getFullYear()}/${String(req.id).padStart(4, '0')}`
      const { error } = await supabase
        .from('student_testimonial_requests')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: college.admin_email,
          certificate_no: certNo
        })
        .eq('id', req.id)

      if (error) throw error
      await loadData()
    } catch (err: any) {
      alert('Approval error: ' + err.message)
    }
  }

  // Reject Request
  const handleReject = async (req: TestimonialRequest) => {
    const reason = prompt('Enter rejection reason:')
    if (reason === null) return

    try {
      const { error } = await supabase
        .from('student_testimonial_requests')
        .update({
          status: 'rejected',
          rejection_reason: reason.trim() || 'Rejected by administration'
        })
        .eq('id', req.id)

      if (error) throw error
      await loadData()
    } catch (err: any) {
      alert('Rejection error: ' + err.message)
    }
  }

  const filteredRequests = requests.filter(r => {
    if (statusTab === 'all') return true
    return r.status === statusTab
  })

  const selectedStudentObj = selectedStudentId ? studentsList.find(s => String(s.student_iid) === String(selectedStudentId)) : null

  return (
    <div style={styles.page}>
      {/* Inject Fonts & Strict A4 Portrait Print Styles for 2 Pages (Bangla & English) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&family=Tiro+Bangla&family=Outfit:wght@400;600;700;800&display=swap');
        
        .kalpurush-font {
          font-family: 'Tiro Bangla', 'Hind Siliguri', 'Kalpurush', 'SolaimanLipi', 'Aneek Bangla', serif !important;
        }

        .english-cert-font {
          font-family: 'Outfit', 'Inter', system-ui, sans-serif !important;
        }

        @page {
          size: A4 portrait;
          margin: 0;
        }

        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            width: 210mm !important;
            height: auto !important;
            overflow: visible !important;
          }
          body * {
            visibility: hidden !important;
          }
          #printable-testimonial-area, #printable-testimonial-area * {
            visibility: visible !important;
          }
          #printable-testimonial-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 210mm !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            box-shadow: none !important;
            border: none !important;
            z-index: 99999 !important;
            overflow: visible !important;
          }
          .printable-a4-area {
            box-shadow: none !important;
            border: none !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .page-break {
            page-break-before: always !important;
            break-before: page !important;
            margin-top: 0 !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <EmsLogo size={36} />
          <button onClick={() => navigate('/dashboard')} style={styles.btnBack}>
            ← Dashboard
          </button>
          <div>
            <h1 style={styles.title}>প্রত্যয়ন পত্র (Testimonial) System</h1>
            <p style={styles.subtitle}>{college?.college_name} (EIIN: {college?.college_eiin})</p>
          </div>
        </div>

        <button onClick={() => setShowApplyModal(true)} style={styles.btnAdd}>
          ➕ New Testimonial Request
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.card}>
          {/* Tabs */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['all', 'pending', 'approved', 'rejected'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setStatusTab(tab)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: statusTab === tab ? '#ff5500' : '#e2e8f0',
                    background: statusTab === tab ? 'rgba(255, 85, 0, 0.1)' : '#fff',
                    color: statusTab === tab ? '#ff5500' : '#475569',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {tab} ({requests.filter(r => tab === 'all' || r.status === tab).length})
                </button>
              ))}
            </div>

            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Total: {filteredRequests.length} applications
            </span>
          </div>

          {/* Requests Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>#</th>
                  <th style={styles.th}>Student Name & Parents</th>
                  <th style={styles.th}>Roll & Group</th>
                  <th style={styles.th}>Session</th>
                  <th style={styles.th}>GPA</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
                  <th style={{ ...styles.th, width: '220px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      Loading testimonial applications...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      No testimonial applications found in this tab.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req, idx) => {
                    const st = req.student
                    return (
                      <tr key={req.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{idx + 1}</td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 800, color: '#0f172a' }}>{st?.student_name_bn || st?.student_name_en || `Student #${req.student_iid}`}</div>
                          {st?.student_name_en && <div style={{ fontSize: '12px', color: '#475569' }}>{st.student_name_en}</div>}
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            F: {st?.father_name_bn || st?.father_name_en || '—'} | M: {st?.mother_name_bn || st?.mother_name_en || '—'}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div>Roll: <strong>{st?.class_roll || '—'}</strong></div>
                          <span style={styles.badgeGroup}>{st?.group || 'General'}</span>
                        </td>
                        <td style={styles.td}>{st?.session || '—'}</td>
                        <td style={{ ...styles.td, fontWeight: 800, color: '#16a34a' }}>{req.gpa || '—'}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {req.status === 'pending' && <span style={styles.badgePending}>Pending Approval</span>}
                          {req.status === 'approved' && <span style={styles.badgeApproved}>Approved</span>}
                          {req.status === 'rejected' && <span style={styles.badgeRejected}>Rejected</span>}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {req.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                              <button onClick={() => handleApprove(req)} style={styles.btnApprove}>
                                ✔ Approve
                              </button>
                              <button onClick={() => handleReject(req)} style={styles.btnReject}>
                                ✖ Reject
                              </button>
                            </div>
                          )}
                          {req.status === 'approved' && (
                            <button onClick={() => handleDirectPrintPdf(req)} style={styles.btnPrint}>
                              📥 Download/Print (2 Pages)
                            </button>
                          )}
                          {req.status === 'rejected' && (
                            <span style={{ fontSize: '11px', color: '#dc2626' }}>{req.rejection_reason || 'Rejected'}</span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* New Application Modal */}
      {showApplyModal && (
        <div style={styles.modalOverlay} onClick={() => setShowApplyModal(false)}>
          <div style={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.2rem', fontWeight: 900 }}>
              ➕ New Testimonial Application
            </h3>

            {notice && (
              <div style={notice.type === 'ok' ? styles.successBox : styles.errorBox}>
                {notice.text}
              </div>
            )}

            <form onSubmit={handleCreateApplication}>
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.label}>Select Student *</label>
                <select
                  style={styles.input}
                  value={selectedStudentId}
                  onChange={e => setSelectedStudentId(e.target.value)}
                  required
                >
                  <option value="">-- Choose Student from Database --</option>
                  {studentsList.map(s => (
                    <option key={s.student_iid} value={s.student_iid}>
                      {s.student_name_bn || s.student_name_en} (Roll: {s.class_roll || '—'}, Group: {s.group || '—'}, Session: {s.session || '—'})
                    </option>
                  ))}
                </select>
              </div>

              {selectedStudentObj && (
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '10px', marginBottom: '14px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                  <div><strong>Student Name:</strong> {selectedStudentObj.student_name_bn || selectedStudentObj.student_name_en}</div>
                  <div><strong>Father:</strong> {selectedStudentObj.father_name_bn || selectedStudentObj.father_name_en || 'N/A'} | <strong>Mother:</strong> {selectedStudentObj.mother_name_bn || selectedStudentObj.mother_name_en || 'N/A'}</div>
                  <div><strong>Group:</strong> {selectedStudentObj.group} | <strong>Roll:</strong> {selectedStudentObj.class_roll} | <strong>Session:</strong> {selectedStudentObj.session}</div>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={styles.label}>GPA / Result *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={gpaInput}
                  onChange={e => setGpaInput(e.target.value)}
                  placeholder="e.g. 5.00"
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styles.btnCancel} onClick={() => setShowApplyModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnSave} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PRINTABLE TESTIMONIAL CERTIFICATE TEMPLATE - 2 PAGES (BANGLA & ENGLISH) */}
      {selectedCert && (
        <div style={styles.modalOverlay} onClick={() => setSelectedCert(null)}>
          <div style={styles.certModalPanel} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '16px' }} className="no-print">
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => selectedCert && handleDirectPrintPdf(selectedCert)} style={styles.btnPrintFull}>
                  📥 Download PDF (2 Pages)
                </button>
                <button onClick={() => window.print()} style={{ ...styles.btnPrintFull, background: '#475569' }}>
                  🖨️ Print
                </button>
              </div>
              <button onClick={() => setSelectedCert(null)} style={styles.btnCancel}>
                Close
              </button>
            </div>

            <div id="printable-testimonial-area">
              {/* PAGE 1: BANGLA VERSION */}
              <div className="printable-a4-area kalpurush-font" style={styles.a4Paper}>
                {/* Header Container matching Screenshot */}
                <div style={styles.certHeaderBox}>
                  {/* Left Side: Official Government Seal of Bangladesh */}
                  <div style={styles.headerLogoSide}>
                    <img 
                      src="/Government_Seal_of_Bangladesh.svg" 
                      alt="Government Seal of Bangladesh" 
                      style={{ width: '68px', height: '68px', objectFit: 'contain' }}
                    />
                  </div>

                  {/* Center Title Block */}
                  <div style={styles.headerCenterText}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                      গণপ্রজাতন্ত্রী বাংলাদেশ সরকার
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>
                      অধ্যক্ষের কার্যালয়
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 900, color: '#15803d', letterSpacing: '-0.2px' }}>
                      {college?.college_name || 'ফেনী সাউথ-ইস্ট কলেজ, ফেনী'}
                    </div>
                    {/* Address, Website, Email, EIIN under College Name */}
                    <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px', fontWeight: 700, lineHeight: '1.4' }}>
                      {college?.college_address && <span>{college.college_address} | </span>}
                      {college?.college_eiin && <span>EIIN: {translateNumberToBengali(college.college_eiin)} | </span>}
                      {college?.email && <span>ইমেইল: {college.email} | </span>}
                      {college?.website && <span>ওয়েবসাইট: {college.website}</span>}
                    </div>
                  </div>

                  {/* Right Side: College Logo / Monogram */}
                  <div style={styles.headerLogoSide}>
                    {college?.college_logo_url ? (
                      <img src={college.college_logo_url} alt="College Logo" style={{ width: '64px', height: '64px', objectFit: 'contain' }} />
                    ) : (
                      <EmsLogo size={62} />
                    )}
                  </div>
                </div>

                {/* Thick Black Horizontal Rule Divider matching Screenshot */}
                <div style={{ borderBottom: '3px solid #000000', margin: '10px 0 16px' }} />

                {/* Date Line Right-Aligned matching Screenshot */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                    স্মারক নং : {translateNumberToBengali(
                      selectedCert.certificate_no?.replace(/^TEST\//i, `${getCollegeAcronym(college?.college_name)}/`) || 
                      `${getCollegeAcronym(college?.college_name)}/${college?.college_eiin}/2026/${String(selectedCert.id).padStart(4, '0')}`
                    )}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>
                    <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '2px' }}>
                      তারিখ : {new Date(selectedCert.approved_at || new Date().toISOString()).toLocaleDateString('bn-BD')}
                    </span>
                  </div>
                </div>

                {/* Main Certificate Heading */}
                <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                  <span style={{
                    fontSize: '21px',
                    fontWeight: 900,
                    color: '#0f172a',
                    borderBottom: '2px solid #0f172a',
                    paddingBottom: '4px',
                    letterSpacing: '0.5px'
                  }}>
                    প্রত্যয়ন পত্র (TESTIMONIAL)
                  </span>
                </div>

                {/* Formal Bangla Certificate Text with clean fallbacks */}
                <div style={{
                  fontSize: '18px',
                  lineHeight: '2.5',
                  color: '#0f172a',
                  textAlign: 'justify',
                  textIndent: '40px',
                  marginBottom: '60px',
                  wordBreak: 'normal',
                  whiteSpace: 'normal'
                }}>
                  এই মর্মে প্রত্যয়ন করা যাইতেছে যে, <strong style={{ fontSize: '20px', borderBottom: '1px dotted #000', padding: '0 4px' }}>
                    {selectedCert.student?.student_name_bn || selectedCert.student?.student_name_en || '—'}
                  </strong>, পিতা: <strong>{selectedCert.student?.father_name_bn || selectedCert.student?.father_name_en || '—'}</strong>, মাতা: <strong>{selectedCert.student?.mother_name_bn || selectedCert.student?.mother_name_en || '—'}</strong>, শ্রেণী রোল: <strong>{translateNumberToBengali(selectedCert.student?.class_roll || '—')}</strong>, শাখা/গ্রুপ: <strong>{translateGroupToBengali(selectedCert.student?.group)}</strong>, শিক্ষাবর্ষ: <strong>{translateNumberToBengali(selectedCert.student?.session || '2025-26')}</strong>। সে এই প্রতিষ্ঠান হইতে উচ্চ মাধ্যমিক পরীক্ষায় অংশগ্রহণ করিয়া জিপিএ <strong style={{ color: '#16a34a', fontSize: '20px' }}>{translateNumberToBengali(selectedCert.gpa || '5.00')}</strong> পাইয়া সফলতার সহিত উত্তীর্ন হইয়াছে।
                  <br />
                  আমার জানা মতে সে রাষ্ট্র বা সমাজবিরোধী কোনো কর্মকাণ্ডের সহিত জড়িত নহে। তাহার নৈতিক চরিত্র উত্তম।
                  <br />
                  আমি তাহার জীবনের সর্বাঙ্গীন সাফল্য ও উজ্জ্বল ভবিষ্যৎ কামনা করি।
                </div>

                {/* Bottom Section with Verification QR Code and Signatures */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
                  {/* Clickable Verification QR Code on the Left */}
                  <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => window.open(window.location.origin + "/verify-testimonial?id=" + selectedCert.id, '_blank')}>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=105x105&data=${encodeURIComponent(window.location.origin + "/verify-testimonial?id=" + selectedCert.id)}`} 
                      alt="Verify QR Code" 
                      style={{ width: '105px', height: '105px', border: '1px solid #cbd5e1', padding: '5px', background: '#fff', display: 'block', margin: '0 auto 6px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 800, display: 'block' }}>স্ক্যান করে যাচাই করুন</span>
                  </div>

                  {/* Principal Signature & Seal on the Right */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '1.5px solid #000', paddingTop: '6px', minWidth: '220px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>
                        {college?.principal_name || 'অধ্যাপক মোহাম্মদ এনামুল হক খন্দকার'}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>
                        অধ্যক্ষ
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                        {college?.college_name || 'ফেনী সাউথ-ইস্ট কলেজ, ফেনী'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PAGE 2: ENGLISH VERSION */}
              <div className="printable-a4-area page-break english-cert-font" style={{ ...styles.a4Paper, marginTop: '40px' }}>
                {/* Header Container English */}
                <div style={styles.certHeaderBox}>
                  {/* Left Side: Official Government Seal of Bangladesh */}
                  <div style={styles.headerLogoSide}>
                    <img 
                      src="/Government_Seal_of_Bangladesh.svg" 
                      alt="Government Seal of Bangladesh" 
                      style={{ width: '68px', height: '68px', objectFit: 'contain' }}
                    />
                  </div>

                  {/* Center Title Block English */}
                  <div style={styles.headerCenterText}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Government of the People's Republic of Bangladesh
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: '2px 0', textTransform: 'uppercase' }}>
                      Office of the Principal
                    </div>
                    <div style={{ fontSize: '21px', fontWeight: 900, color: '#15803d', letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                      {college?.college_name_en || college?.college_name || 'Feni South-East College, Feni'}
                    </div>
                    {/* Address, Website, Email, EIIN under College Name */}
                    <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px', fontWeight: 700, lineHeight: '1.4' }}>
                      {(college?.college_address_en || college?.college_address) && <span>{college?.college_address_en || college?.college_address} | </span>}
                      {college?.college_eiin && <span>EIIN: {college.college_eiin} | </span>}
                      {college?.email && <span>Email: {college.email} | </span>}
                      {college?.website && <span>Website: {college.website}</span>}
                    </div>
                  </div>

                  {/* Right Side: College Logo / Monogram */}
                  <div style={styles.headerLogoSide}>
                    {college?.college_logo_url ? (
                      <img src={college.college_logo_url} alt="College Logo" style={{ width: '64px', height: '64px', objectFit: 'contain' }} />
                    ) : (
                      <EmsLogo size={62} />
                    )}
                  </div>
                </div>

                {/* Thick Black Horizontal Rule Divider */}
                <div style={{ borderBottom: '3px solid #000000', margin: '10px 0 16px' }} />

                {/* Date Line Right-Aligned */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                    Memo No: {selectedCert.certificate_no || `TEST/${college?.college_eiin}/2026/0001`}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                    <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '2px' }}>
                      Date: {new Date(selectedCert.approved_at || new Date().toISOString()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Main Certificate Heading English */}
                <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 900,
                    color: '#0f172a',
                    borderBottom: '2px solid #0f172a',
                    paddingBottom: '4px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>
                    Testimonial Certificate
                  </span>
                </div>

                {/* Formal English Certificate Text */}
                <div style={{
                  fontSize: '16px',
                  lineHeight: '2.4',
                  color: '#0f172a',
                  textAlign: 'justify',
                  textIndent: '40px',
                  marginBottom: '60px'
                }}>
                  This is to certify that <strong style={{ fontSize: '18px', borderBottom: '1px dotted #000', padding: '0 4px' }}>
                    {selectedCert.student?.student_name_en || selectedCert.student?.student_name_bn || '—'}
                  </strong>, Son/Daughter of <strong>{selectedCert.student?.father_name_en || selectedCert.student?.father_name_bn || '—'}</strong> and <strong>{selectedCert.student?.mother_name_en || selectedCert.student?.mother_name_bn || '—'}</strong>, Class Roll: <strong>{selectedCert.student?.class_roll || '—'}</strong>, Group: <strong>{selectedCert.student?.group || 'Science'}</strong>, Session: <strong>{selectedCert.student?.session || '2025-26'}</strong>, successfully passed the Higher Secondary Certificate (HSC) Examination from this institution securing GPA <strong style={{ color: '#16a34a', fontSize: '18px' }}>{selectedCert.gpa || '5.00'}</strong> out of 5.00.
                  <br />
                  To the best of my knowledge, he/she did not take part in any activity subversive of the state or of discipline. His/Her moral character is good.
                  <br />
                  I wish him/her every success and bright future in life.
                </div>

                {/* Bottom Section with Verification QR Code and Signatures English */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
                  {/* Clickable Verification QR Code on the Left */}
                  <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => window.open(window.location.origin + "/verify-testimonial?id=" + selectedCert.id, '_blank')}>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=105x105&data=${encodeURIComponent(window.location.origin + "/verify-testimonial?id=" + selectedCert.id)}`} 
                      alt="Verify QR Code" 
                      style={{ width: '105px', height: '105px', border: '1px solid #cbd5e1', padding: '5px', background: '#fff', display: 'block', margin: '0 auto 6px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 800, display: 'block' }}>Scan to Verify</span>
                  </div>

                  {/* Principal Signature & Seal on the Right English */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '1.5px solid #000', paddingTop: '6px', minWidth: '220px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>
                        {college?.principal_name_en || college?.principal_name || 'Professor Mohammad Enamul Hoque Khondakar'}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>
                        Principal
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                        {college?.college_name_en || college?.college_name || 'Feni South-East College, Feni'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
    color: '#0f172a'
  },
  header: {
    background: '#ffffff',
    padding: '16px 32px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 50
  },
  btnBack: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0f172a'
  },
  subtitle: {
    margin: 0,
    fontSize: '11px',
    color: '#ff5500',
    fontWeight: 800
  },
  btnAdd: {
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '12px',
    fontWeight: 800,
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255, 85, 0, 0.3)'
  },
  main: {
    maxWidth: '1100px',
    margin: '32px auto',
    padding: '0 20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    background: '#f1f5f9',
    color: '#475569',
    fontWeight: 800,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '12px 14px',
    verticalAlign: 'middle'
  },
  badgeGroup: {
    padding: '3px 8px',
    borderRadius: '6px',
    background: 'rgba(255, 85, 0, 0.1)',
    color: '#ff5500',
    fontWeight: 800,
    fontSize: '11px'
  },
  badgePending: {
    padding: '4px 10px',
    borderRadius: '999px',
    background: '#fef3c7',
    color: '#d97706',
    fontWeight: 800,
    fontSize: '12px'
  },
  badgeApproved: {
    padding: '4px 10px',
    borderRadius: '999px',
    background: '#dcfce7',
    color: '#15803d',
    fontWeight: 800,
    fontSize: '12px'
  },
  badgeRejected: {
    padding: '4px 10px',
    borderRadius: '999px',
    background: '#fee2e2',
    color: '#b91c1c',
    fontWeight: 800,
    fontSize: '12px'
  },
  btnApprove: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#16a34a',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  btnReject: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  btnPrint: {
    padding: '6px 14px',
    borderRadius: '8px',
    border: '1px solid #2563eb',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.65)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalPanel: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '28px',
    width: '460px',
    maxWidth: '92%',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25)'
  },
  certModalPanel: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '28px',
    width: '880px',
    maxWidth: '95%',
    maxHeight: '92vh',
    overflowY: 'auto'
  },
  a4Paper: {
    width: '210mm',
    minHeight: '297mm',
    background: '#ffffff',
    padding: '18mm 18mm',
    boxSizing: 'border-box',
    margin: '0 auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px'
  },
  certHeaderBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerLogoSide: {
    width: '70px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  headerCenterText: {
    textAlign: 'center',
    flex: 1
  },
  btnPrintFull: {
    padding: '10px 24px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '14px',
    cursor: 'pointer'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#475569'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fff'
  },
  btnCancel: {
    padding: '9px 18px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer'
  },
  btnSave: {
    padding: '9px 22px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer'
  },
  successBox: {
    padding: '12px 16px',
    background: '#dcfce7',
    color: '#15803d',
    borderRadius: '12px',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: 700
  },
  errorBox: {
    padding: '12px 16px',
    background: '#fee2e2',
    color: '#b91c1c',
    borderRadius: '12px',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: 700
  }
}
